import { selectPortfolioPrecision } from "@shared/slices/portfolio.slice";
import { MOBILE_SEARCH, SORT_ORDER_MAPPING } from "common/constants";
import Table from "common/Table/Table";
import { ColumnsType } from "antd/es/table";
import AccountTableChart from "features/portfolio/Accounts/AccountTableChart";
import AccountAndExchange from "features/portfolio/Accounts/AccountAndExchange";
import useMedia from "utils/hooks/useMedia";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import {
  ClientStatus,
  ConsolidatedAccountData,
  LanguageNameSpaces,
  PrecisionType,
} from "@shared/types";
import React, { ReactNode, useContext } from "react";
import { TableLoadingEmpty } from "common/styles";
import Flex from "common/Boxes/Flex";
import AccountActions from "features/configuration/accounts/AccountActions";
import {
  AccountsSort,
  PortfolioAccountData,
} from "@shared/features/portfolio/types";
import AccountStatusTag from "features/configuration/accounts/AccountConfigurationDrawer/AccountStatusTag";
import { Skeleton } from "common/Skeleton/Skeleton";
import WhitelistTag from "features/configuration/accounts/AccountConfigurationDrawer/WhitelistTag";
import ChartSkeleton from "features/portfolio/Accounts/ChartSkeleton";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { AccountsPageContext } from "features/portfolio/Accounts/AccountsPage.context";
import { getTableOnChangeFunction } from "common/Table/utils";
import { useTheme } from "styled-components";
import { NewLookTrendCell } from "features/portfolio/NewLook/NewLookTrendCell.tsx";

interface AccountsTableProps {
  data: PortfolioAccountData[];
  totalHeader: "total_in_btc" | "total_in_usd";
  availableHeader: "available_in_btc" | "available_in_usd";
  loading: boolean;
  accountLoading: boolean;
  getTotalAmount: (accountData: ConsolidatedAccountData) => React.JSX.Element;
  openAccountExtraDetails?: (accountData: ConsolidatedAccountData) => void;
  emptyState?: React.JSX.Element;
}

const AccountsTable = ({
  data,
  totalHeader,
  availableHeader,
  loading,
  accountLoading,
  getTotalAmount,
  openAccountExtraDetails,
  emptyState,
}: AccountsTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { isNewLook } = useTheme();
  const numberAlign = isNewLook ? "right" : undefined;

  const { sortBy, setSortBy, sortDirection, setSortDirection } =
    useContext(AccountsPageContext)!;

  const isAccountInvalid = (account: PortfolioAccountData) =>
    account.client_status && account.client_status !== ClientStatus.ACTIVE;

  const getValueOrNa = (account: PortfolioAccountData, value: ReactNode) => {
    if (isAccountInvalid(account)) {
      return t("common.n_a");
    } else if (accountLoading || account.isLoading) {
      return (
        <Skeleton paragraph={false} active={true} style={{ width: 100 }} />
      );
    }
    return value;
  };

  const columns: ColumnsType<PortfolioAccountData> = [
    {
      title: isMobile ? t("accounts.title") : t("accounts.name"),
      dataIndex: "client",
      key: "client",
      showSorterTooltip: false,
      sortOrder:
        sortBy === AccountsSort.NAME
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      sorter: (a, b) => sortByStringProperty(a, b, "client"),
      render: (account: string, coinData) => (
        <AccountAndExchange account={account} exchange={coinData.exchange} />
      ),
    },
    {
      title: t("accounts.status"),
      dataIndex: "client_status",
      key: "client_status",
      showSorterTooltip: false,
      sortOrder:
        sortBy === AccountsSort.STATUS
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      sorter: (a, b) =>
        +(b.client_status === ClientStatus.ACTIVE) -
        +(a.client_status === ClientStatus.ACTIVE),
      render: (status, account) => (
        <>
          <AccountStatusTag
            status={status}
            style={{ width: "fit-content" }}
            size={isNewLook ? "small" : undefined}
          />
          {account.has_assigned_ips === false && account.is_valid && (
            <WhitelistTag
              withBgColor={false}
              animated={false}
              exchange={account.exchange}
            />
          )}
        </>
      ),
    },
    {
      title: t("accounts.total"),
      dataIndex: totalHeader,
      key: "total",
      align: numberAlign,
      showSorterTooltip: false,
      sortOrder:
        sortBy === AccountsSort.TOTAL
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      render: (_, accountData) =>
        getValueOrNa(accountData, getTotalAmount(accountData)),
      sorter: (a, b) => sortByNumberProperty(a, b, totalHeader),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            justifyContent: isNewLook ? "flex-end" : undefined,
          }}
        >
          {t("accounts.available")}
          <HelperTooltip
            analyticsFieldName={t("accounts.available")}
            analyticsPageName={"Accounts"}
            description={t("accounts.available_tooltip")}
          />
        </div>
      ),
      dataIndex: availableHeader,
      key: "available",
      align: numberAlign,
      showSorterTooltip: false,
      sortOrder:
        sortBy === AccountsSort.AVAILABLE
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      render: (available: number, accountData) =>
        getValueOrNa(
          accountData,
          <TextWithPrecisionAndIcon
            precisionSelector={selectPortfolioPrecision}
            precisionType={PrecisionType.AMOUNT}
            text={available}
            round={true}
          />
        ),
      sorter: (a, b) => sortByNumberProperty(a, b, availableHeader),
    },
    {
      title: t("accounts.balance_24h"),
      dataIndex: "graph_data",
      key: "change",
      align: numberAlign,
      showSorterTooltip: false,
      sortOrder:
        sortBy === AccountsSort.CHANGE
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      render: (_, clientData) => {
        if (isAccountInvalid(clientData)) return t("common.n_a");
        if (accountLoading || clientData.isLoading) {
          return <ChartSkeleton />;
        }
        if (isNewLook) return <NewLookTrendCell data={clientData.graph_data} />;
        return clientData.graph_data ? (
          <AccountTableChart data={clientData.graph_data} />
        ) : undefined;
      },
      sorter: (a, b) => sortByNumberProperty(a, b, "graph_data.change[0]"),
      width: CHART_COLUMN_WIDTH,
    },
    {
      title: "",
      dataIndex: "id",
      key: "action",
      width: 40,
      render: (_, clientData) => (
        <Flex
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          style={{ gridGap: 6 }}
        >
          <AccountActions accountId={clientData.client_id} />
        </Flex>
      ),
    },
  ];

  const handleChange = getTableOnChangeFunction<AccountsSort>(
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    FIELD_NAME_TO_SORT
  );

  return (
    <Table<PortfolioAccountData>
      columns={columns}
      dataSource={data}
      loading={loading}
      onChange={handleChange}
      rowClassName="table-half-padding u-cursorPointer"
      onRow={(accountData) => ({
        onClick: () => openAccountExtraDetails?.(accountData),
      })}
      locale={{ emptyText: emptyState || <TableLoadingEmpty /> }}
      itemsType={ITEMS_TYPE}
    />
  );
};

export default AccountsTable;

const ITEMS_TYPE = "accounts";
const CHART_COLUMN_WIDTH = 150;

const FIELD_NAME_TO_SORT = {
  client: AccountsSort.NAME,
  client_status: AccountsSort.STATUS,
  total: AccountsSort.TOTAL,
  available: AccountsSort.AVAILABLE,
  change: AccountsSort.CHANGE,
};
